import { IPackage } from '~src/packages/models/IPackage';

import { OriginCanonicalizer } from './OriginCanonicalizer';

// This module is a client-side, non-authoritative preview. It recomputes compliance on every
// render from data already exposed by ListPackagesService's response (package
// `requestedOrigins` + each extension's `sourceUrl`). The server never persists a compliance
// verdict -- it enforces live at serve time instead (see PackageContentController.java) and
// disables non-compliant packages via a backgrounder when the allow-list changes -- so this
// preview intentionally stays a best-effort client-side estimate, not a cache of a server value.

export interface IPackageComplianceResult {
  readonly pkg: IPackage;
  /**
   * True only if every origin the package declares (package-level `requestedOrigins` plus
   * each extension's absolute external `sourceUrl`) canonicalizes to a strict origin that
   * is a member of the candidate allowed-origins set.
   */
  readonly isCompliant: boolean;
  /** Canonical origins successfully extracted from the package's declared sources. */
  readonly declaredOrigins: readonly string[];
  /**
   * True if any declared source could not canonicalize to a strict origin (e.g. a
   * wildcard or other non-canonical CSP keyword in `requestedOrigins`, or a malformed
   * `sourceUrl`). Such packages are always non-compliant: unlike the backend's best-effort
   * PackageOriginExtractor (which silently skips these for its reverse-lookup lens), a
   * compliance *evaluator* must not treat "couldn't determine the origin" as "compliant" --
   * see the phase1 design doc's explicit backfill rule that no finite exact-origin allowlist
   * can authorize a wildcard source.
   */
  readonly hasNonCanonicalSource: boolean;
}

export class PackageComplianceEvaluator {

  private constructor() {}

  /**
   * Evaluates every package in `packages` against the candidate `allowedOrigins` set.
   * `allowedOrigins` should already be canonical (as produced by
   * {@link OriginCanonicalizer.canonicalizeOrigin}); non-canonical members simply won't
   * match anything.
   */
  public static evaluateAll(
    packages: readonly IPackage[],
    allowedOrigins: ReadonlySet<string>
  ): IPackageComplianceResult[] {
    return packages.map(pkg => PackageComplianceEvaluator.evaluate(pkg, allowedOrigins));
  }

  public static evaluate(pkg: IPackage, allowedOrigins: ReadonlySet<string>): IPackageComplianceResult {
    const { origins, hasNonCanonicalSource } = PackageComplianceEvaluator.extractDeclaredOrigins(pkg);
    const declaredOrigins = Array.from(origins);
    const isCompliant = !hasNonCanonicalSource && declaredOrigins.every(origin => allowedOrigins.has(origin));
    return { pkg, isCompliant, declaredOrigins, hasNonCanonicalSource };
  }

  /**
   * Mirrors PackageOriginExtractor.extractOrigins (package `requestedOrigins` field, plus
   * each extension's absolute http(s) `sourceUrl`), but -- deliberately, see
   * {@link IPackageComplianceResult.hasNonCanonicalSource} -- treats a non-canonicalizable
   * declared source as forcing non-compliance rather than silently skipping it.
   */
  private static extractDeclaredOrigins(pkg: IPackage): { origins: Set<string>, hasNonCanonicalSource: boolean } {
    const origins = new Set<string>();
    let hasNonCanonicalSource = false;

    if (pkg.requestedOrigins) {
      for (const token of OriginCanonicalizer.splitOrigins(pkg.requestedOrigins)) {
        const canonical = OriginCanonicalizer.tryCanonicalizeOrigin(token);
        if (canonical === undefined) {
          hasNonCanonicalSource = true;
        } else {
          origins.add(canonical);
        }
      }
    }

    for (const ext of pkg.extensions?.extension ?? []) {
      const sourceUrl = ext.sourceUrl;
      if (!sourceUrl || !(sourceUrl.startsWith('http://') || sourceUrl.startsWith('https://'))) {
        // Not an absolute external URL (e.g. a hosted/bundled extension resolved to a
        // relative content path) -- contributes no origin, same as the Java extractor.
        continue;
      }
      const canonical = OriginCanonicalizer.tryCanonicalizeOriginOfUrl(sourceUrl);
      if (canonical === undefined) {
        hasNonCanonicalSource = true;
      } else {
        origins.add(canonical);
      }
    }

    return { origins, hasNonCanonicalSource };
  }
}
